/**
 * Navigation audit helpers for header, footer, and profile menu by role context.
 */

import {
  CLIENT_FORBIDDEN_FOOTER_HREFS,
  CLIENT_FORBIDDEN_FOOTER_LABEL_KEYS,
  FOOTER_CONTEXT_CLIENT,
  footerLinkHrefs,
  resolveFooterContext,
  resolveFooterSections,
} from './footerNavigation';
import {
  resolveClientHeaderQuickLinks,
  resolveHeaderNavLinks,
} from './roleExperience';
import {
  canAccessClientDashboard,
  showsClientHeaderQuickLinks,
} from './roleExperience';
import { canAccessOwnerPricingLane } from './pricingLaneDisplay';
import {
  ROUTE_ADMIN,
  ROUTE_CLIENT_DASHBOARD,
  ROUTE_OWNER_DASHBOARD,
  isGuestContext,
  showsAdminNavigation,
  showsOwnerNavigation,
} from './roleContext';

export const CLIENT_FORBIDDEN_PROFILE_HREFS = [
  ROUTE_OWNER_DASHBOARD,
  '/owner/plans',
  '/owner/listings',
  '/owner/listings/new',
  '/pricing',
  '/register?role=owner',
];

export const CLIENT_FORBIDDEN_PROFILE_TEST_IDS = [
  'owner-dashboard-link',
  'owner-plans-link',
  'pricing-link',
];

export const GENERIC_DASHBOARD_LABELS = ['Dashboard'];

function link(href, labelKey, defaultLabel, testId, visible = true) {
  return { href, labelKey, defaultLabel, testId, visible };
}

export function resolveProfileMenuLinks(input) {
  const { user, isAdmin, viewAsRole } = input;
  if (!user) {
    return [];
  }

  const roleState = { user, isAdmin, viewAsRole };
  const guestCtx = isGuestContext(roleState);
  const showsClientHeader = input.showsClientHeaderQuickLinks
    ?? showsClientHeaderQuickLinks({ ...roleState, isGuestContext: guestCtx });
  const clientDashboardAccess = input.canAccessClientDashboard
    ?? canAccessClientDashboard(roleState);
  const showsOwnerNav = input.showsOwnerNav ?? showsOwnerNavigation(roleState);
  const showsAdminNav = input.showsAdminNav ?? showsAdminNavigation(roleState);
  const ownerPricing = input.canAccessOwnerPricingLane
    ?? canAccessOwnerPricingLane(roleState);

  const showClientDashboardInMenu = clientDashboardAccess && !showsClientHeader;
  const clientDashboardVisible = showClientDashboardInMenu;

  return [
    link(ROUTE_CLIENT_DASHBOARD, 'nav.client_home_search', 'My Home Search', 'client-dashboard-link', clientDashboardVisible),
    link(ROUTE_OWNER_DASHBOARD, 'nav.owner_dashboard', 'Owner Dashboard', 'owner-dashboard-link', showsOwnerNav),
    link('/owner/plans', 'nav.upgrade_plan', 'Owner Plans', 'owner-plans-link', showsOwnerNav),
    link(ROUTE_ADMIN, 'nav.admin_dashboard', 'Admin Dashboard', 'admin-link', showsAdminNav),
    link('/notifications', 'nav.inbox', 'Inbox', 'notifications-link', true),
    link('/favorites', 'nav.my_favorites', 'Favorites', 'favorites-link', true),
    link('/bookings', 'nav.my_bookings', 'My Booking Requests', 'bookings-link', true),
    link('/settings', 'nav.settings', 'Settings', 'settings-link', true),
    link('/pricing', 'nav.pricing', 'Pricing', 'pricing-link', ownerPricing),
  ].filter((item) => item.visible);
}

export function profileMenuLinkHrefs(roleState) {
  return resolveProfileMenuLinks(roleState).map((item) => item.href);
}

export function profileMenuTestIds(roleState) {
  return resolveProfileMenuLinks(roleState).map((item) => item.testId);
}

export function profileMenuHasForbiddenClientLinks(roleState) {
  if (resolveFooterContext(roleState) !== FOOTER_CONTEXT_CLIENT) {
    return false;
  }
  const hrefs = profileMenuLinkHrefs(roleState);
  const testIds = profileMenuTestIds(roleState);
  return (
    CLIENT_FORBIDDEN_PROFILE_HREFS.some((forbidden) => hrefs.includes(forbidden))
    || CLIENT_FORBIDDEN_PROFILE_TEST_IDS.some((id) => testIds.includes(id))
  );
}

export function headerHasOwnerPricingForClient(roleState) {
  if (resolveFooterContext(roleState) !== FOOTER_CONTEXT_CLIENT) {
    return false;
  }
  return canAccessOwnerPricingLane(roleState);
}

export function headerClientLinks(roleState) {
  const showsClient = Boolean(roleState.user && roleState.showsClientHeaderQuickLinks);
  return resolveHeaderNavLinks({ showsClientHeaderQuickLinks: showsClient });
}

export function headerHasDuplicateDashboardLabels(roleState) {
  const headerLinks = headerClientLinks(roleState);
  const profileLinks = resolveProfileMenuLinks(roleState);
  const labels = [
    ...headerLinks.map((l) => l.defaultLabel),
    ...profileLinks.map((l) => l.defaultLabel),
  ];
  const genericDashboardCount = labels.filter((label) => GENERIC_DASHBOARD_LABELS.includes(label)).length;
  return genericDashboardCount > 1;
}

export function auditNavigationForContext(roleState) {
  const context = resolveFooterContext(roleState);
  const footerHrefs = footerLinkHrefs(roleState);
  const profileHrefs = profileMenuLinkHrefs(roleState);

  return {
    context,
    footerHrefs,
    profileHrefs,
    footerSections: resolveFooterSections(roleState),
    headerLinks: headerClientLinks(roleState),
    profileLinks: resolveProfileMenuLinks(roleState),
    violations: collectNavigationViolations(roleState),
  };
}

export function collectNavigationViolations(roleState) {
  const violations = [];
  const context = resolveFooterContext(roleState);

  if (context === FOOTER_CONTEXT_CLIENT) {
    const footerHrefs = footerLinkHrefs(roleState);
    CLIENT_FORBIDDEN_FOOTER_HREFS.forEach((forbidden) => {
      if (footerHrefs.includes(forbidden)) {
        violations.push(`client footer includes forbidden href: ${forbidden}`);
      }
    });

    resolveFooterSections(roleState).forEach((section) => {
      if (CLIENT_FORBIDDEN_FOOTER_LABEL_KEYS.includes(section.titleKey)) {
        violations.push(`client footer includes forbidden section: ${section.titleKey}`);
      }
      section.links.forEach((item) => {
        if (CLIENT_FORBIDDEN_FOOTER_LABEL_KEYS.includes(item.labelKey)) {
          violations.push(`client footer includes forbidden label: ${item.labelKey}`);
        }
      });
    });

    if (profileMenuHasForbiddenClientLinks(roleState)) {
      violations.push('client profile menu includes owner-only links');
    }

    if (headerHasOwnerPricingForClient(roleState)) {
      violations.push('client header/profile exposes owner pricing lane');
    }

    if (headerHasDuplicateDashboardLabels(roleState)) {
      violations.push('duplicate generic Dashboard labels in visible navigation');
    }
  }

  return violations;
}

export function assertClientNavigationSafe(roleState) {
  return collectNavigationViolations(roleState);
}

export function resolveClientHeaderQuickLinkLabels() {
  return resolveClientHeaderQuickLinks().map((link) => link.defaultLabel);
}

export { CLIENT_FORBIDDEN_FOOTER_HREFS, CLIENT_FORBIDDEN_FOOTER_LABEL_KEYS } from './footerNavigation';
